import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  Grid, Element, ElementType, Particle, Agent, 
  TornadoAgent, MeteorAgent, BlackHoleAgent, RainCloudAgent, ExplosionAgent, BombAgent,
  FreezeRayAgent, EarthquakeAgent, TsunamiAgent,
  Settings
} from './types';
import {
  GRID_WIDTH,
  GRID_HEIGHT,
  INITIAL_ELEMENTS,
  FLOWER_ELEMENT,
  SIMULATION_INTERVAL,
  AMBIENT_TEMPERATURE,
  FIRE_TEMPERATURE,
  LAVA_TEMPERATURE,
  LAVA_SOLIDIFY_POINT,
  ICE_TARGET_TEMPERATURE,
  SAND_MELT_POINT,
  ICE_MELT_POINT,
  WATER_BOIL_POINT,
  LIGHTNING_TEMPERATURE,
  OIL_IGNITE_POINT,
  GUNPOWDER_IGNITE_POINT,
  WAX_MELT_POINT,
  COAL_IGNITE_POINT,
  MAGMA_SOLIDIFY_POINT,
  PREBUILT_SCENES,
} from './constants';
import Toolbar from './components/Toolbar';
import SimulationCanvas from './components/SimulationCanvas';
import TodoList from './components/TodoList';
import TemperatureDisplay from './components/TemperatureDisplay';
import SettingsPanel from './components/SettingsPanel';
import SceneSelector from './components/SceneSelector';
import { roadmapData } from './roadmap';
import { audioManager, SpellType } from './audio/AudioManager';
import { 
  createChunkManager, markCellModified, updateChunkSleepState, 
  wakeAllChunks, shouldProcessChunk, getChunkBounds 
} from './chunkManager';

const createEmptyGrid = (): Grid => {
  return Array.from({ length: GRID_HEIGHT }, () =>
    Array.from({ length: GRID_WIDTH }, () => ({ elementId: ElementType.EMPTY, temperature: 20 }))
  );
};

const initializeTaskStatus = (): Record<string, 'done' | 'todo'> => {
  const status: Record<string, 'done' | 'todo'> = {};
  roadmapData.forEach(phase => {
    phase.tasks.forEach(task => {
      status[task.id] = task.status;
    });
  });
  return status;
};

const createUpdatedArray = (): boolean[][] => {
  const arr: boolean[][] = new Array(GRID_HEIGHT);
  for (let y = 0; y < GRID_HEIGHT; y++) {
    arr[y] = new Array(GRID_WIDTH).fill(false);
  }
  return arr;
};

const DEFAULT_SETTINGS: Settings = {
  audio: { enabled: false, volume: 50 },
  graphics: { glowEffects: true, particleLimit: 2000 },
  simulation: { speed: 1 },
};

const App: React.FC = () => {
  const [grid, setGrid] = useState<Grid>(createEmptyGrid);
  const [elements, setElements] = useState<Element[]>([...INITIAL_ELEMENTS, FLOWER_ELEMENT]);
  const [selectedElementId, setSelectedElementId] = useState<string>(ElementType.SAND);
  const [brushSize, setBrushSize] = useState(5);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isTodoListOpen, setIsTodoListOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isSceneSelectorOpen, setIsSceneSelectorOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [taskStatus, setTaskStatus] = useState<Record<string, 'done' | 'todo'>>(initializeTaskStatus);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [cellSize, setCellSize] = useState(0);
  const [roomTemperature, setRoomTemperature] = useState(AMBIENT_TEMPERATURE);
  const [screenShake, setScreenShake] = useState({ x: 0, y: 0 });
  const [isDrawing, setIsDrawing] = useState(false);
  const drawPosRef = useRef<{ x: number; y: number } | null>(null);
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);
  const [usedElements, setUsedElements] = useState<Set<string>>(new Set());

  // Chunk manager for performance optimization
  const chunkManagerRef = useRef(createChunkManager());

  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const updatedRef = useRef<boolean[][]>(createUpdatedArray());
  const frameCountRef = useRef(0);

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('magic-sandbox-settings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings({ ...DEFAULT_SETTINGS, ...parsed });
      } catch (e) {
        console.error('Failed to load settings:', e);
      }
    }
  }, []);

  // Save settings to localStorage when changed
  useEffect(() => {
    localStorage.setItem('magic-sandbox-settings', JSON.stringify(settings));
    audioManager.updateSettings({
      enabled: settings.audio.enabled,
      masterVolume: settings.audio.volume,
      sfxVolume: settings.audio.volume,
      ambientVolume: settings.audio.volume * 0.5,
    });
  }, [settings]);

  // Initialize audio on first user interaction
  useEffect(() => {
    const handleFirstInteraction = () => {
      audioManager.initialize();
      if (settings.audio.enabled) {
        audioManager.startAmbientLoop();
      }
      window.removeEventListener('click', handleFirstInteraction);
      window.removeEventListener('keydown', handleFirstInteraction);
    };
    window.addEventListener('click', handleFirstInteraction);
    window.addEventListener('keydown', handleFirstInteraction);
    return () => {
      window.removeEventListener('click', handleFirstInteraction);
      window.removeEventListener('keydown', handleFirstInteraction);
    };
  }, [settings.audio.enabled]);

  const triggerScreenShake = useCallback((intensity: number) => {
    const shakeCount = Math.floor(intensity * 5);
    let count = 0;
    const shake = () => {
      if (count < shakeCount) {
        const factor = 1 - count / shakeCount;
        setScreenShake({
          x: (Math.random() - 0.5) * intensity * factor * 10,
          y: (Math.random() - 0.5) * intensity * factor * 10,
        });
        count++;
        requestAnimationFrame(shake);
      } else {
        setScreenShake({ x: 0, y: 0 });
      }
    };
    shake();
  }, []);

  const elementMap = useMemo(() => new Map(elements.map(el => [el.id, el])), [elements]);

  const calculateRoomTemperature = useCallback((grid: Grid): number => {
    let totalTemp = 0;
    let particleCount = 0;
    const sampleStep = 4;
    for (let y = 0; y < GRID_HEIGHT; y += sampleStep) {
      for (let x = 0; x < GRID_WIDTH; x += sampleStep) {
        const particle = grid[y][x];
        if (particle.elementId !== ElementType.EMPTY && particle.elementId !== ElementType.WALL) {
          totalTemp += particle.temperature || AMBIENT_TEMPERATURE;
          particleCount++;
        }
      }
    }
    return particleCount > 0 ? Math.round(totalTemp / particleCount) : AMBIENT_TEMPERATURE;
  }, []);

  // Responsive canvas logic
  useEffect(() => {
    const calculateCellSize = () => {
      if (canvasContainerRef.current) {
        const containerWidth = canvasContainerRef.current.offsetWidth;
        const containerHeight = canvasContainerRef.current.offsetHeight;
        const sizeX = Math.floor(containerWidth / GRID_WIDTH);
        const sizeY = Math.floor(containerHeight / GRID_HEIGHT);
        setCellSize(Math.max(1, Math.min(sizeX, sizeY)));
      }
    };
    calculateCellSize();
    window.addEventListener('resize', calculateCellSize);
    return () => window.removeEventListener('resize', calculateCellSize);
  }, []);

  // Main simulation tick
  useEffect(() => {
    const simulationTick = () => {
      setAgents(prevAgents => {
        const updatedAgents: Agent[] = [];

        for (const agent of prevAgents) {
          if (agent.type === 'BOMB_AGENT') {
            if (agent.timer <= 0) continue;
          } else if ('lifetime' in agent && agent.lifetime <= 1) {
            continue;
          }

          if (agent.type === 'TORNADO') {
            updatedAgents.push({
              ...agent,
              x: agent.x + agent.vx,
              y: agent.y + agent.vy,
              lifetime: agent.lifetime - 1,
            });
          } else if (agent.type === 'METEOR') {
            const newY = agent.y + agent.vy;
            const newX = agent.x + agent.vx;
            updatedAgents.push({
              ...agent,
              x: newX,
              y: newY,
              vy: Math.min(agent.vy + 0.3, 4),
              lifetime: agent.lifetime - 1,
            });
          } else if (agent.type === 'BLACKHOLE') {
            updatedAgents.push({
              ...agent,
              lifetime: agent.lifetime - 1,
            });
          } else if (agent.type === 'RAINCLOUD') {
            let newX = agent.x + agent.vx;
            if (newX < 10 || newX > GRID_WIDTH - 10) {
              updatedAgents.push({
                ...agent,
                x: newX,
                vx: -agent.vx,
                lifetime: agent.lifetime - 1,
              });
            } else {
              updatedAgents.push({
                ...agent,
                x: newX,
                lifetime: agent.lifetime - 1,
              });
            }
          } else if (agent.type === 'EXPLOSION') {
            const newRadius = Math.min(agent.radius + 2, agent.maxRadius);
            updatedAgents.push({
              ...agent,
              radius: newRadius,
              lifetime: agent.lifetime - 1,
            });
          } else if (agent.type === 'BOMB_AGENT') {
            const newTimer = agent.timer - 1;
            const gravity = 0.3;
            const newVy = Math.min(agent.vy + gravity, 3);
            let newY = agent.y + newVy;
            let finalVy = newVy;
            if (newY >= GRID_HEIGHT - 1) {
              newY = GRID_HEIGHT - 1;
              finalVy = 0;
            }
            if (newTimer <= 0) {
              const explosionAgent: ExplosionAgent = {
                id: Date.now(),
                type: 'EXPLOSION',
                x: agent.x,
                y: newY,
                radius: 1,
                maxRadius: 15,
                lifetime: 20,
              };
              updatedAgents.push(explosionAgent);
              triggerScreenShake(2);
              audioManager.playSpellSound('EXPLOSION');
            } else {
              updatedAgents.push({ ...agent, y: newY, vy: finalVy, timer: newTimer });
            }
          } else if (agent.type === 'FREEZE_RAY') {
            updatedAgents.push({
              ...agent,
              lifetime: agent.lifetime - 1,
            });
          } else if (agent.type === 'EARTHQUAKE') {
            const newRadius = Math.min(agent.radius + 1, agent.maxRadius);
            updatedAgents.push({
              ...agent,
              radius: newRadius,
              lifetime: agent.lifetime - 1,
            });
          } else if (agent.type === 'TSUNAMI') {
            updatedAgents.push({
              ...agent,
              x: agent.x + agent.vx,
              lifetime: agent.lifetime - 1,
            });
          } else {
            updatedAgents.push({ ...agent, lifetime: agent.lifetime - 1 });
          }
        }

        frameCountRef.current++;
        const frameCount = frameCountRef.current;
        const chunkManager = chunkManagerRef.current;
        chunkManager.frameCount = frameCount;

        setGrid(prevGrid => {
          const newGrid: Grid = new Array(GRID_HEIGHT);
          for (let y = 0; y < GRID_HEIGHT; y++) {
            newGrid[y] = new Array(GRID_WIDTH);
            for (let x = 0; x < GRID_WIDTH; x++) {
              const p = prevGrid[y][x];
              newGrid[y][x] = { elementId: p.elementId, temperature: p.temperature, lifetime: p.lifetime, age: p.age, vx: p.vx };
            }
          }

          const updated = updatedRef.current;
          for (let y = 0; y < GRID_HEIGHT; y++) {
            for (let x = 0; x < GRID_WIDTH; x++) {
              updated[y][x] = false;
            }
          }

          const getDensityById = (id: ElementType | string): number => elementMap.get(id)?.density ?? 1.0;

          const isOutOfBounds = (px: number, py: number) => px < 0 || px >= GRID_WIDTH || py < 0 || py >= GRID_HEIGHT;
          const get = (px: number, py: number): Particle => isOutOfBounds(px, py) ? { elementId: ElementType.WALL, temperature: AMBIENT_TEMPERATURE } : newGrid[py][px];
          const set = (px: number, py: number, p: Particle) => { 
            if (!isOutOfBounds(px, py)) {
              newGrid[py][px] = p;
              markCellModified(chunkManager, px, py);
            }
          };

          const swap = (x1: number, y1: number, x2: number, y2: number) => {
            if (isOutOfBounds(x1, y1) || isOutOfBounds(x2, y2)) return;
            const p1 = newGrid[y1][x1];
            const p2 = newGrid[y2][x2];
            newGrid[y2][x2] = p1;
            newGrid[y1][x1] = p2;
            updated[y1][x1] = true;
            updated[y2][x2] = true;
            markCellModified(chunkManager, x1, y1);
            markCellModified(chunkManager, x2, y2);
          };

          // --- 1. Simplified Thermal Diffusion (run every other frame for performance) ---
          const runThermal = frameCount % 2 === 0;
          for (let y = 0; y < GRID_HEIGHT; y++) {
            for (let x = 0; x < GRID_WIDTH; x++) {
              const particle = newGrid[y][x];
              if (particle.elementId === ElementType.EMPTY) continue;

              if (particle.elementId === ElementType.FIRE) {
                particle.temperature = FIRE_TEMPERATURE;
                if (runThermal) {
                  for (let dy = -1; dy <= 1; dy++) {
                    for (let dx = -1; dx <= 1; dx++) {
                      if (dx === 0 && dy === 0) continue;
                      if (!isOutOfBounds(x + dx, y + dy)) {
                        const n = newGrid[y + dy][x + dx];
                        if (n.elementId !== ElementType.EMPTY && n.elementId !== ElementType.WALL) {
                          n.temperature += (FIRE_TEMPERATURE - n.temperature) * 0.25;
                        }
                      }
                    }
                  }
                }
              } else if (particle.elementId === ElementType.LAVA || particle.elementId === ElementType.MAGMA) {
                const temp = particle.elementId === ElementType.MAGMA ? LAVA_TEMPERATURE * 1.2 : LAVA_TEMPERATURE;
                particle.temperature = Math.max(temp * 0.9, particle.temperature);
                if (runThermal) {
                  for (let dy = -1; dy <= 1; dy++) {
                    for (let dx = -1; dx <= 1; dx++) {
                      if (dx === 0 && dy === 0) continue;
                      if (!isOutOfBounds(x + dx, y + dy)) {
                        const n = newGrid[y + dy][x + dx];
                        if (n.elementId !== ElementType.EMPTY && n.elementId !== ElementType.WALL) {
                          n.temperature += (temp - n.temperature) * 0.12;
                        }
                      }
                    }
                  }
                }
              } else if (particle.elementId === ElementType.ICE) {
                particle.temperature = Math.min(ICE_TARGET_TEMPERATURE * 0.5, particle.temperature);
                if (runThermal) {
                  for (let dy = -1; dy <= 1; dy++) {
                    for (let dx = -1; dx <= 1; dx++) {
                      if (dx === 0 && dy === 0) continue;
                      if (!isOutOfBounds(x + dx, y + dy)) {
                        const n = newGrid[y + dy][x + dx];
                        if (n.elementId !== ElementType.EMPTY && n.elementId !== ElementType.WALL) {
                          n.temperature += (ICE_TARGET_TEMPERATURE - n.temperature) * 0.12;
                        }
                      }
                    }
                  }
                }
              } else if (runThermal) {
                particle.temperature += (AMBIENT_TEMPERATURE - particle.temperature) * 0.003;
              }
            }
          }

          // --- 2. Agent Effects (Tornado, Meteor, Black Hole, etc.) ---
          // Tornado Simulation
          for (const agent of updatedAgents) {
            if (agent.type !== 'TORNADO') continue;
            const tornado = agent as TornadoAgent;
            const radius = Math.ceil(tornado.radius);
            const minX = Math.max(0, Math.floor(tornado.x) - radius);
            const maxX = Math.min(GRID_WIDTH - 1, Math.floor(tornado.x) + radius);
            const minY = Math.max(0, Math.floor(tornado.y) - radius);
            const maxY = Math.min(GRID_HEIGHT - 1, Math.floor(tornado.y) + radius);

            for (let y = minY; y <= maxY; y++) {
              for (let x = minX; x <= maxX; x++) {
                if (updated[y][x]) continue;
                const particle = newGrid[y][x];
                if (particle.elementId === ElementType.EMPTY || particle.elementId === ElementType.WALL) continue;

                const dx = x - tornado.x;
                const dy = y - tornado.y;
                const distSq = dx * dx + dy * dy;
                const radiusSq = tornado.radius * tornado.radius;

                if (distSq > 0 && distSq < radiusSq) {
                  const dist = Math.sqrt(distSq);
                  const falloff = 1 - (dist / tornado.radius);
                  const strength = tornado.strength * 3;

                  const pullForce = falloff * strength * 0.5;
                  const rotForce = falloff * strength * tornado.rotation * 1.5;

                  let forceX = -(dx / dist) * pullForce - (dy / dist) * rotForce;
                  let forceY = -(dy / dist) * pullForce + (dx / dist) * rotForce;

                  const density = getDensityById(particle.elementId);
                  const moveChance = Math.min(0.8, (Math.abs(forceX) + Math.abs(forceY)) / (density + 0.5));

                  if (Math.random() < moveChance) {
                    const targetX = Math.max(0, Math.min(GRID_WIDTH - 1, x + Math.round(forceX)));
                    const targetY = Math.max(0, Math.min(GRID_HEIGHT - 1, y + Math.round(forceY)));

                    if (!updated[targetY][targetX]) {
                      const target = newGrid[targetY][targetX];
                      if (target.elementId === ElementType.EMPTY) {
                        swap(x, y, targetX, targetY);
                      } else if (target.elementId !== ElementType.WALL && Math.random() < 0.4) {
                        swap(x, y, targetX, targetY);
                      }
                    }
                  }
                }
              }
            }
          }

          // Meteor Simulation
          for (const agent of updatedAgents) {
            if (agent.type !== 'METEOR') continue;
            const meteor = agent as MeteorAgent;
            const mx = Math.round(meteor.x);
            const my = Math.round(meteor.y);

            if (my >= GRID_HEIGHT - 2 || (!isOutOfBounds(mx, my) && get(mx, my).elementId !== ElementType.EMPTY && get(mx, my).elementId !== ElementType.FIRE)) {
              const explosionRadius = meteor.size * 3;
              for (let dy = -explosionRadius; dy <= explosionRadius; dy++) {
                for (let dx = -explosionRadius; dx <= explosionRadius; dx++) {
                  const dist = Math.sqrt(dx * dx + dy * dy);
                  if (dist <= explosionRadius && !isOutOfBounds(mx + dx, my + dy)) {
                    const target = get(mx + dx, my + dy);
                    if (target.elementId !== ElementType.WALL) {
                      if (dist < explosionRadius * 0.4) {
                        set(mx + dx, my + dy, { elementId: ElementType.FIRE, lifetime: 30 + Math.random() * 30, temperature: FIRE_TEMPERATURE });
                      } else if (Math.random() < 0.5) {
                        set(mx + dx, my + dy, { elementId: ElementType.EMBER, lifetime: 20 + Math.random() * 20, temperature: FIRE_TEMPERATURE * 0.8 });
                      } else if (dist < explosionRadius * 0.7) {
                        set(mx + dx, my + dy, { elementId: ElementType.SMOKE, lifetime: 60, temperature: 300 });
                      }
                    }
                  }
                }
              }
              triggerScreenShake(2);
              audioManager.playExplosion(1.5);
              meteor.lifetime = 0;
            } else {
              for (let s = 0; s < meteor.size; s++) {
                for (let dy = -s; dy <= s; dy++) {
                  for (let dx = -s; dx <= s; dx++) {
                    if (Math.sqrt(dx * dx + dy * dy) <= s && !isOutOfBounds(mx + dx, my + dy)) {
                      set(mx + dx, my + dy, { elementId: ElementType.FIRE, lifetime: 15, temperature: FIRE_TEMPERATURE });
                    }
                  }
                }
              }
            }
          }

          // Black Hole Simulation
          for (const agent of updatedAgents) {
            if (agent.type !== 'BLACKHOLE') continue;
            const bh = agent as BlackHoleAgent;
            const effectRadius = bh.radius * 2;
            const bhX = Math.floor(bh.x);
            const bhY = Math.floor(bh.y);
            const minX = Math.max(0, bhX - effectRadius);
            const maxX = Math.min(GRID_WIDTH - 1, bhX + effectRadius);
            const minY = Math.max(0, bhY - effectRadius);
            const maxY = Math.min(GRID_HEIGHT - 1, bhY + effectRadius);

            const isExpiring = bh.lifetime <= 15;
            const eventHorizon = 6;

            for (let y = minY; y <= maxY; y++) {
              for (let x = minX; x <= maxX; x++) {
                if (updated[y][x]) continue;
                const particle = newGrid[y][x];
                if (particle.elementId === ElementType.EMPTY || particle.elementId === ElementType.WALL) continue;

                const dx = x - bh.x;
                const dy = y - bh.y;
                const distSq = dx * dx + dy * dy;

                if (!isExpiring && distSq < eventHorizon * eventHorizon) {
                  newGrid[y][x] = { elementId: ElementType.EMPTY, temperature: AMBIENT_TEMPERATURE };
                  updated[y][x] = true;
                  markCellModified(chunkManager, x, y);
                } else if (distSq < effectRadius * effectRadius && distSq > 0) {
                  const dist = Math.sqrt(distSq);
                  const invDistSq = 1 / (distSq * 0.01 + 1);
                  const baseStrength = bh.strength * 8;

                  const normX = dx / dist;
                  const normY = dy / dist;

                  let forceX: number, forceY: number;
                  let moveChance: number;

                  if (isExpiring) {
                    const expulsionStrength = baseStrength * 2 * (1 - bh.lifetime / 15);
                    const tangentX = -normY;
                    const tangentY = normX;
                    forceX = normX * expulsionStrength + tangentX * expulsionStrength * 0.5;
                    forceY = normY * expulsionStrength + tangentY * expulsionStrength * 0.5;
                    moveChance = 0.9;
                  } else {
                    const pullMag = invDistSq * baseStrength;
                    const swirlMag = invDistSq * baseStrength * 1.2;
                    forceX = -normX * pullMag + normY * swirlMag;
                    forceY = -normY * pullMag - normX * swirlMag;
                    const density = getDensityById(particle.elementId);
                    const densityFactor = 1 / (density * 0.3 + 0.5);
                    moveChance = Math.min(0.95, pullMag * densityFactor * 0.3);
                  }

                  if (Math.random() < moveChance) {
                    const speed = Math.min(isExpiring ? 6 : 4, Math.sqrt(forceX * forceX + forceY * forceY));
                    const moveX = Math.round(forceX / (Math.abs(forceX) + Math.abs(forceY) + 0.1) * speed * 2);
                    const moveY = Math.round(forceY / (Math.abs(forceX) + Math.abs(forceY) + 0.1) * speed * 2);
                    const targetX = Math.max(0, Math.min(GRID_WIDTH - 1, x + moveX));
                    const targetY = Math.max(0, Math.min(GRID_HEIGHT - 1, y + moveY));

                    if (!updated[targetY][targetX]) {
                      const target = newGrid[targetY][targetX];
                      if (target.elementId === ElementType.EMPTY) {
                        swap(x, y, targetX, targetY);
                      } else if (target.elementId !== ElementType.WALL && Math.random() < 0.5) {
                        swap(x, y, targetX, targetY);
                      }
                    }
                  }
                }
              }
            }
          }

          // Rain Cloud Simulation
          for (const agent of updatedAgents) {
            if (agent.type !== 'RAINCLOUD') continue;
            const cloud = agent as RainCloudAgent;
            if (Math.random() < 0.4) {
              const dropX = Math.round(cloud.x + (Math.random() - 0.5) * cloud.width);
              const dropY = Math.round(cloud.y + 3);
              if (!isOutOfBounds(dropX, dropY) && get(dropX, dropY).elementId === ElementType.EMPTY) {
                set(dropX, dropY, { elementId: ElementType.WATER, temperature: AMBIENT_TEMPERATURE });
              }
            }
          }

          // Explosion Simulation
          for (const agent of updatedAgents) {
            if (agent.type !== 'EXPLOSION') continue;
            const exp = agent as ExplosionAgent;
            const currentRadius = exp.radius;
            for (let angle = 0; angle < Math.PI * 2; angle += 0.15) {
              const ex = Math.round(exp.x + Math.cos(angle) * currentRadius);
              const ey = Math.round(exp.y + Math.sin(angle) * currentRadius);
              if (!isOutOfBounds(ex, ey)) {
                const target = get(ex, ey);
                if (target.elementId !== ElementType.WALL && target.elementId !== ElementType.EMPTY) {
                  if (Math.random() < 0.6) {
                    const flammable = elementMap.get(target.elementId)?.flammable;
                    if (flammable) {
                      set(ex, ey, { elementId: ElementType.FIRE, lifetime: 30, temperature: FIRE_TEMPERATURE });
                    } else {
                      set(ex, ey, { elementId: ElementType.SMOKE, lifetime: 40, temperature: 400 });
                    }
                  }
                }
              }
            }
          }

          // Freeze Ray Simulation
          for (const agent of updatedAgents) {
            if (agent.type !== 'FREEZE_RAY') continue;
            const freezeRay = agent as FreezeRayAgent;
            for (const point of freezeRay.path) {
              const fx = Math.round(point.x);
              const fy = Math.round(point.y);
              for (let dy = -2; dy <= 2; dy++) {
                for (let dx = -2; dx <= 2; dx++) {
                  const nx = fx + dx;
                  const ny = fy + dy;
                  if (!isOutOfBounds(nx, ny)) {
                    const target = get(nx, ny);
                    if (target.elementId === ElementType.WATER) {
                      set(nx, ny, { elementId: ElementType.ICE, temperature: ICE_TARGET_TEMPERATURE });
                    } else if (target.elementId === ElementType.LAVA) {
                      set(nx, ny, { elementId: ElementType.STONE, temperature: AMBIENT_TEMPERATURE });
                    } else if (target.elementId === ElementType.MAGMA) {
                      set(nx, ny, { elementId: ElementType.STONE, temperature: AMBIENT_TEMPERATURE });
                    } else if (target.elementId === ElementType.STEAM) {
                      set(nx, ny, { elementId: ElementType.WATER, temperature: AMBIENT_TEMPERATURE });
                    }
                  }
                }
              }
            }
          }

          // Earthquake Simulation
          for (const agent of updatedAgents) {
            if (agent.type !== 'EARTHQUAKE') continue;
            const earthquake = agent as EarthquakeAgent;
            const currentRadius = earthquake.radius;
            for (let angle = 0; angle < Math.PI * 2; angle += 0.1) {
              const ex = Math.round(earthquake.x + Math.cos(angle) * currentRadius);
              const ey = Math.round(earthquake.y + Math.sin(angle) * currentRadius);
              if (!isOutOfBounds(ex, ey)) {
                const target = get(ex, ey);
                if (target.elementId !== ElementType.WALL && target.elementId !== ElementType.EMPTY) {
                  // Displace particles upward and outward
                  const displacementY = Math.max(0, ey - 3);
                  const displacementX = ex + (Math.random() > 0.5 ? 1 : -1);
                  if (!isOutOfBounds(displacementX, displacementY) && get(displacementX, displacementY).elementId === ElementType.EMPTY) {
                    set(displacementX, displacementY, { ...target });
                    set(ex, ey, { elementId: ElementType.EMPTY, temperature: AMBIENT_TEMPERATURE });
                  }
                }
              }
            }
          }

          // Tsunami Simulation
          for (const agent of updatedAgents) {
            if (agent.type !== 'TSUNAMI') continue;
            const tsunami = agent as TsunamiAgent;
            const tx = Math.round(tsunami.x);
            const ty = Math.round(tsunami.y);
            // Create wall of water
            for (let dy = 0; dy < tsunami.height; dy++) {
              for (let dx = -tsunami.width / 2; dx < tsunami.width / 2; dx++) {
                const nx = tx + dx;
                const ny = ty - dy;
                if (!isOutOfBounds(nx, ny)) {
                  const target = get(nx, ny);
                  if (target.elementId === ElementType.EMPTY || target.elementId === ElementType.FIRE) {
                    set(nx, ny, { elementId: ElementType.WATER, temperature: AMBIENT_TEMPERATURE, vx: tsunami.vx });
                  }
                }
              }
            }
          }

          // --- 3. Particle Physics Simulation ---
          for (let y = GRID_HEIGHT - 1; y >= 0; y--) {
            const xOrder = Math.random() < 0.5 ? (x: number) => x : (x: number) => GRID_WIDTH - 1 - x;
            for (let i = 0; i < GRID_WIDTH; i++) {
              const x = xOrder(i);
              if (updated[y][x]) continue;

              const particle = newGrid[y][x];

              // Phase transitions
              if (particle.elementId === ElementType.SAND && particle.temperature >= SAND_MELT_POINT) {
                set(x, y, { elementId: ElementType.GLASS, temperature: particle.temperature });
                continue;
              }
              if (particle.elementId === ElementType.WATER && particle.temperature >= WATER_BOIL_POINT) {
                set(x, y, { elementId: ElementType.STEAM, lifetime: 120 + Math.random() * 40, temperature: particle.temperature });
                continue;
              }
              if (particle.elementId === ElementType.WATER && particle.temperature <= ICE_MELT_POINT) {
                set(x, y, { elementId: ElementType.ICE, temperature: particle.temperature });
                continue;
              }
              if (particle.elementId === ElementType.ICE && particle.temperature > ICE_MELT_POINT) {
                set(x, y, { elementId: ElementType.WATER, temperature: particle.temperature });
                continue;
              }
              if (particle.elementId === ElementType.LAVA && particle.temperature < LAVA_SOLIDIFY_POINT) {
                set(x, y, { elementId: ElementType.STONE, temperature: particle.temperature });
                continue;
              }
              if (particle.elementId === ElementType.MAGMA && particle.temperature < MAGMA_SOLIDIFY_POINT) {
                set(x, y, { elementId: ElementType.STONE, temperature: particle.temperature });
                continue;
              }
              // NEW ELEMENTS: Phase transitions
              if (particle.elementId === ElementType.WAX && particle.temperature >= WAX_MELT_POINT) {
                set(x, y, { elementId: ElementType.OIL, temperature: particle.temperature });
                continue;
              }
              if (particle.elementId === ElementType.COAL && particle.temperature >= COAL_IGNITE_POINT) {
                set(x, y, { elementId: ElementType.FIRE, lifetime: 200 + Math.random() * 100, temperature: FIRE_TEMPERATURE });
                continue;
              }

              switch (particle.elementId) {
                case ElementType.SAND:
                case ElementType.DIRT:
                case ElementType.GLASS:
                case ElementType.SALT: {
                  const density = getDensityById(particle.elementId);
                  if (!isOutOfBounds(x, y + 1) && !updated[y + 1][x] && getDensityById(newGrid[y + 1][x].elementId) < density) {
                    swap(x, y, x, y + 1);
                  } else {
                    const dir = Math.random() > 0.5 ? 1 : -1;
                    if (!isOutOfBounds(x + dir, y + 1) && !updated[y + 1][x + dir] && getDensityById(newGrid[y + 1][x + dir].elementId) < density) {
                      swap(x, y, x + dir, y + 1);
                    } else if (!isOutOfBounds(x - dir, y + 1) && !updated[y + 1][x - dir] && getDensityById(newGrid[y + 1][x - dir].elementId) < density) {
                      swap(x, y, x - dir, y + 1);
                    }
                  }
                  break;
                }

                case ElementType.STONE: {
                  const density = getDensityById(ElementType.STONE);
                  if (!isOutOfBounds(x, y + 1) && !updated[y + 1][x] && getDensityById(newGrid[y + 1][x].elementId) < density) {
                    swap(x, y, x, y + 1);
                  }
                  break;
                }

                case ElementType.ICE: {
                  const iceNeighbors = [[0, 1], [0, -1], [1, 0], [-1, 0]];
                  let melted = false;
                  for (const [dx, dy] of iceNeighbors) {
                    if (isOutOfBounds(x + dx, y + dy)) continue;
                    const neighbor = newGrid[y + dy][x + dx];
                    if (neighbor.elementId === ElementType.LAVA || neighbor.elementId === ElementType.MAGMA) {
                      set(x, y, { elementId: ElementType.STEAM, lifetime: 120, temperature: 150 });
                      set(x + dx, y + dy, { elementId: ElementType.STONE, temperature: LAVA_SOLIDIFY_POINT - 10 });
                      melted = true;
                      break;
                    }
                  }
                  if (melted) break;

                  let floated = false;
                  if (!isOutOfBounds(x, y - 1) && !updated[y - 1][x]) {
                    const particleAbove = newGrid[y - 1][x];
                    if ((particleAbove.elementId === ElementType.WATER || particleAbove.elementId === ElementType.ACID) && getDensityById(particleAbove.elementId) > getDensityById(ElementType.ICE)) {
                      swap(x, y, x, y - 1);
                      floated = true;
                    }
                  }
                  if (floated) break;

                  const iceDensity = getDensityById(ElementType.ICE);
                  if (!isOutOfBounds(x, y + 1) && !updated[y + 1][x] && getDensityById(newGrid[y + 1][x].elementId) < iceDensity) {
                    swap(x, y, x, y + 1);
                  } else {
                    const dirFall = Math.random() > 0.5 ? 1 : -1;
                    if (!isOutOfBounds(x + dirFall, y + 1) && !updated[y + 1][x + dirFall] && getDensityById(newGrid[y + 1][x + dirFall].elementId) < iceDensity) {
                      swap(x, y, x + dirFall, y + 1);
                    } else if (!isOutOfBounds(x - dirFall, y + 1) && !updated[y + 1][x - dirFall] && getDensityById(newGrid[y + 1][x - dirFall].elementId) < iceDensity) {
                      swap(x, y, x - dirFall, y + 1);
                    }
                  }
                  break;
                }

                case ElementType.MUD: {
                  if (Math.random() < 0.3) {
                    const dir = particle.vx || (Math.random() < 0.5 ? 1 : -1);
                    if (!isOutOfBounds(x, y + 1) && !updated[y + 1][x] && getDensityById(newGrid[y + 1][x].elementId) < getDensityById(particle.elementId)) {
                      swap(x, y, x, y + 1);
                    } else if (!isOutOfBounds(x + dir, y) && !updated[y][x + dir] && newGrid[y][x + dir].elementId === ElementType.EMPTY) {
                      swap(x, y, x + dir, y);
                      if (!isOutOfBounds(x + dir, y)) newGrid[y][x + dir].vx = dir;
                    }
                  }

                  let hasWaterNeighbor = false;
                  const neighbors = [[0, 1], [0, -1], [1, 0], [-1, 0]];
                  for (const [dx, dy] of neighbors) {
                    if (!isOutOfBounds(x + dx, y + dy) && newGrid[y + dy][x + dx].elementId === ElementType.WATER) {
                      hasWaterNeighbor = true;
                      break;
                    }
                  }
                  if (hasWaterNeighbor) {
                    set(x, y, { ...particle, lifetime: 300 });
                  } else {
                    const newLifetime = (particle.lifetime || 300) - 1;
                    if (newLifetime <= 0) {
                      set(x, y, { elementId: ElementType.DIRT, temperature: particle.temperature });
                    } else {
                      set(x, y, { ...particle, lifetime: newLifetime });
                    }
                  }
                  break;
                }

                case ElementType.LAVA:
                case ElementType.MAGMA: {
                  const lavaNeighbors = [[0, 1], [0, -1], [1, 0], [-1, 0]];
                  for (const [dx, dy] of lavaNeighbors) {
                    if (isOutOfBounds(x + dx, y + dy)) continue;
                    const neighbor = newGrid[y + dy][x + dx];
                    const neighborProps = elementMap.get(neighbor.elementId);

                    if (neighbor.elementId === ElementType.WATER) {
                      set(x + dx, y + dy, { elementId: ElementType.STEAM, lifetime: 120, temperature: 150 });
                      set(x, y, { ...particle, temperature: particle.temperature - 50 });
                    } else if (neighbor.elementId === ElementType.ICE) {
                      set(x + dx, y + dy, { elementId: ElementType.WATER, temperature: 10 });
                      set(x, y, { elementId: ElementType.STONE, temperature: particle.temperature - 200 });
                    } else if (neighborProps?.flammable && Math.random() < 0.25) {
                      set(x + dx, y + dy, { elementId: ElementType.FIRE, lifetime: 40, temperature: FIRE_TEMPERATURE });
                    }
                  }

                  if (Math.random() < 0.2) {
                    const density = getDensityById(particle.elementId);
                    const dir = particle.vx || (Math.random() < 0.5 ? 1 : -1);
                    if (!isOutOfBounds(x, y + 1) && !updated[y + 1][x] && getDensityById(newGrid[y + 1][x].elementId) < density) {
                      swap(x, y, x, y + 1);
                    } else if (!isOutOfBounds(x + dir, y) && !updated[y][x + dir] && getDensityById(newGrid[y][x + dir].elementId) < density) {
                      swap(x, y, x + dir, y);
                      if (!isOutOfBounds(x + dir, y)) newGrid[y][x + dir].vx = dir;
                    }
                  }
                  break;
                }

                case ElementType.WATER: {
                  const waterDensity = getDensityById(ElementType.WATER);
                  const dir = particle.vx || (Math.random() < 0.5 ? 1 : -1);
                  const canDisplace = (tx: number, ty: number) =>
                    !isOutOfBounds(tx, ty) &&
                    !updated[ty][tx] &&
                    getDensityById(newGrid[ty][tx].elementId) < waterDensity;

                  if (canDisplace(x, y + 1)) {
                    swap(x, y, x, y + 1);
                    break;
                  }

                  if (canDisplace(x + dir, y + 1)) {
                    swap(x, y, x + dir, y + 1);
                    if (!isOutOfBounds(x + dir, y + 1)) newGrid[y + 1][x + dir].vx = dir;
                    break;
                  }
                  if (canDisplace(x - dir, y + 1)) {
                    swap(x, y, x - dir, y + 1);
                    if (!isOutOfBounds(x - dir, y + 1)) newGrid[y + 1][x - dir].vx = -dir;
                    break;
                  }

                  if (!isOutOfBounds(x + dir, y) && !updated[y][x + dir] && newGrid[y][x + dir].elementId === ElementType.EMPTY) {
                    swap(x, y, x + dir, y);
                    if (!isOutOfBounds(x + dir, y)) newGrid[y][x + dir].vx = dir;
                  } else if (!isOutOfBounds(x - dir, y) && !updated[y][x - dir] && newGrid[y][x - dir].elementId === ElementType.EMPTY) {
                    swap(x, y, x - dir, y);
                    if (!isOutOfBounds(x - dir, y)) newGrid[y][x - dir].vx = -dir;
                  } else {
                    let moved = false;
                    for (const lateralDir of [dir, -dir]) {
                      const step1 = x + lateralDir;
                      const step2 = x + lateralDir * 2;
                      if (
                        !isOutOfBounds(step2, y) &&
                        !updated[y][step2] &&
                        newGrid[y][step1].elementId === ElementType.EMPTY &&
                        newGrid[y][step2].elementId === ElementType.EMPTY
                      ) {
                        swap(x, y, step2, y);
                        newGrid[y][step2].vx = lateralDir;
                        moved = true;
                        break;
                      }
                    }
                    if (!moved && newGrid[y][x]) newGrid[y][x].vx = 0;
                  }
                  break;
                }

                case ElementType.ACID: {
                  const neighbors = [[0, 1], [0, -1], [1, 0], [-1, 0]];
                  let corroded = false;
                  for (const [dx, dy] of neighbors) {
                    if (isOutOfBounds(x + dx, y + dy)) continue;
                    const neighbor = newGrid[y + dy][x + dx];
                    if ([ElementType.SAND, ElementType.STONE, ElementType.WOOD, ElementType.PLANT, ElementType.DIRT, ElementType.MUD, ElementType.WAX, ElementType.FUSE, ElementType.COAL].includes(neighbor.elementId as ElementType)) {
                      if (Math.random() > 0.7) {
                        set(x + dx, y + dy, { elementId: ElementType.EMPTY, temperature: AMBIENT_TEMPERATURE });
                        corroded = true;
                      }
                    }
                  }
                  if (corroded && Math.random() > 0.8) {
                    set(x, y, { elementId: ElementType.EMPTY, temperature: AMBIENT_TEMPERATURE });
                  } else if (!isOutOfBounds(x, y + 1) && !updated[y + 1][x] && getDensityById(newGrid[y + 1][x].elementId) < getDensityById(ElementType.ACID)) {
                    swap(x, y, x, y + 1);
                  }
                  break;
                }

                case ElementType.OIL: {
                  if (particle.temperature >= OIL_IGNITE_POINT) {
                    set(x, y, { elementId: ElementType.FIRE, lifetime: 80 + Math.random() * 40, temperature: FIRE_TEMPERATURE });
                    break;
                  }
                  const oilDir = particle.vx || (Math.random() < 0.5 ? 1 : -1);
                  const oilDensity = getDensityById(ElementType.OIL);
                  if (!isOutOfBounds(x, y + 1) && !updated[y + 1][x] && getDensityById(newGrid[y + 1][x].elementId) < oilDensity) {
                    swap(x, y, x, y + 1);
                  } else if (!isOutOfBounds(x + oilDir, y) && !updated[y][x + oilDir] && (newGrid[y][x + oilDir].elementId === ElementType.EMPTY || newGrid[y][x + oilDir].elementId === ElementType.WATER)) {
                    swap(x, y, x + oilDir, y);
                    if (!isOutOfBounds(x + oilDir, y)) newGrid[y][x + oilDir].vx = oilDir;
                  } else if (!isOutOfBounds(x - oilDir, y) && !updated[y][x - oilDir] && (newGrid[y][x - oilDir].elementId === ElementType.EMPTY || newGrid[y][x - oilDir].elementId === ElementType.WATER)) {
                    swap(x, y, x - oilDir, y);
                    if (!isOutOfBounds(x - oilDir, y)) newGrid[y][x - oilDir].vx = -oilDir;
                  }
                  break;
                }

                case ElementType.GUNPOWDER: {
                  if (particle.temperature >= GUNPOWDER_IGNITE_POINT) {
                    const gpRadius = 8;
                    for (let dy = -gpRadius; dy <= gpRadius; dy++) {
                      for (let dx = -gpRadius; dx <= gpRadius; dx++) {
                        const dist = Math.sqrt(dx * dx + dy * dy);
                        if (dist <= gpRadius && !isOutOfBounds(x + dx, y + dy)) {
                          const target = get(x + dx, y + dy);
                          if (target.elementId !== ElementType.WALL) {
                            if (target.elementId === ElementType.GUNPOWDER) {
                              set(x + dx, y + dy, { elementId: ElementType.FIRE, lifetime: 10, temperature: FIRE_TEMPERATURE * 1.5 });
                            } else if (dist < gpRadius * 0.5) {
                              set(x + dx, y + dy, { elementId: ElementType.FIRE, lifetime: 25 + Math.random() * 15, temperature: FIRE_TEMPERATURE });
                            } else if (Math.random() < 0.6) {
                              set(x + dx, y + dy, { elementId: ElementType.SMOKE, lifetime: 50, temperature: 400 });
                            }
                          }
                        }
                      }
                    }
                    triggerScreenShake(1.5);
                    audioManager.playExplosion(1.2);
                    break;
                  }
                  const gpDensity = getDensityById(ElementType.GUNPOWDER);
                  if (!isOutOfBounds(x, y + 1) && !updated[y + 1][x] && getDensityById(newGrid[y + 1][x].elementId) < gpDensity) {
                    swap(x, y, x, y + 1);
                  } else {
                    const gpDir = Math.random() > 0.5 ? 1 : -1;
                    if (!isOutOfBounds(x + gpDir, y + 1) && !updated[y + 1][x + gpDir] && getDensityById(newGrid[y + 1][x + gpDir].elementId) < gpDensity) {
                      swap(x, y, x + gpDir, y + 1);
                    }
                  }
                  break;
                }

                case ElementType.METAL: {
                  const metalDensity = getDensityById(ElementType.METAL);
                  if (!isOutOfBounds(x, y + 1) && !updated[y + 1][x] && getDensityById(newGrid[y + 1][x].elementId) < metalDensity) {
                    swap(x, y, x, y + 1);
                  }
                  break;
                }

                case ElementType.EMBER: {
                  const emberLife = (particle.lifetime || 20) - 1;
                  if (emberLife <= 0) {
                    set(x, y, { elementId: ElementType.SMOKE, lifetime: 30, temperature: 150 });
                  } else {
                    set(x, y, { ...particle, lifetime: emberLife });
                    const emberDensity = getDensityById(ElementType.FIRE);
                    if (Math.random() < 0.6) {
                      const moveDir = Math.random() < 0.5 ? -1 : 1;
                      const moveUp = Math.random() < 0.3;
                      const targetY = moveUp ? y - 1 : y + 1;
                      const targetX = x + moveDir;
                      if (!isOutOfBounds(targetX, targetY) && !updated[targetY][targetX] && getDensityById(newGrid[targetY][targetX].elementId) < emberDensity) {
                        swap(x, y, targetX, targetY);
                      }
                    }
                    const emberNeighbors = [[0, 1], [0, -1], [1, 0], [-1, 0]];
                    for (const [dx, dy] of emberNeighbors) {
                      if (isOutOfBounds(x + dx, y + dy)) continue;
                      const neighbor = newGrid[y + dy][x + dx];
                      const neighborProps = elementMap.get(neighbor.elementId);
                      if (neighborProps?.flammable && Math.random() < 0.15) {
                        set(x + dx, y + dy, { elementId: ElementType.FIRE, lifetime: 30, temperature: FIRE_TEMPERATURE });
                      }
                    }
                  }
                  break;
                }

                case ElementType.FIRE: {
                  const fireNeighbors = [[0, -1], [1, 0], [-1, 0], [0, 1]];
                  let extinguished = false;
                  for (const [dx, dy] of fireNeighbors) {
                    if (isOutOfBounds(x + dx, y + dy)) continue;
                    const neighbor = newGrid[y + dy][x + dx];
                    if (neighbor.elementId === ElementType.WATER) {
                      set(x + dx, y + dy, { elementId: ElementType.EMPTY, temperature: AMBIENT_TEMPERATURE });
                      set(x, y, { elementId: ElementType.STEAM, lifetime: 120 + Math.random() * 40, temperature: 110 });
                      extinguished = true;
                      break;
                    }
                  }
                  if (extinguished) break;

                  const newLifetimeFire = (particle.lifetime || 20) - 1;
                  if (newLifetimeFire <= 0) {
                    set(x, y, { elementId: ElementType.SMOKE, lifetime: 60, temperature: 200 });
                  } else {
                    set(x, y, { ...particle, lifetime: newLifetimeFire, temperature: FIRE_TEMPERATURE });
                    for (const [dx, dy] of fireNeighbors) {
                      if (isOutOfBounds(x + dx, y + dy)) continue;
                      const neighbor = newGrid[y + dy][x + dx];
                      const neighborElementProps = elementMap.get(neighbor.elementId);
                      if (neighborElementProps?.flammable) {
                        if (Math.random() > 0.5) {
                          set(x + dx, y + dy, { elementId: ElementType.FIRE, lifetime: 20 + Math.random() * 20, temperature: FIRE_TEMPERATURE });
                        }
                      }
                    }
                  }
                  break;
                }

                case ElementType.STEAM:
                case ElementType.SMOKE: {
                  const newLifetimeGas = (particle.lifetime || 100) - 1;
                  if (newLifetimeGas <= 0) {
                    set(x, y, { elementId: ElementType.EMPTY, temperature: particle.temperature });
                  } else {
                    const p = { ...particle, lifetime: newLifetimeGas };
                    set(x, y, p);
                    const pDensity = getDensityById(p.elementId);
                    const moveUp = !isOutOfBounds(x, y - 1) && !updated[y - 1][x] && getDensityById(newGrid[y - 1][x].elementId) < pDensity;

                    const sideDir = p.vx || (Math.random() < 0.5 ? 1 : -1);
                    const moveSide = !isOutOfBounds(x + sideDir, y) && !updated[y][x + sideDir] && getDensityById(newGrid[y][x + sideDir].elementId) < pDensity;

                    if (moveUp && (Math.random() < 0.8 || !moveSide)) {
                      swap(x, y, x, y - 1);
                    } else if (moveSide) {
                      swap(x, y, x + sideDir, y);
                      if (!isOutOfBounds(x + sideDir, y)) newGrid[y][x + sideDir].vx = Math.random() < 0.1 ? -sideDir : sideDir;
                    } else {
                      if (newGrid[y][x]) newGrid[y][x].vx = -sideDir;
                    }
                  }
                  break;
                }

                case ElementType.PLANT: {
                  let hasWater = false;
                  let hasSoil = false;
                  let isOptimalSoil = false;
                  let waterCount = 0;
                  const growthNeighbors = [[0, 1], [1, 0], [-1, 0], [1, 1], [-1, 1], [-1, -1], [1, -1]];
                  for (const [dx, dy] of growthNeighbors) {
                    if (isOutOfBounds(x + dx, y + dy)) continue;
                    const n = newGrid[y + dy][x + dx];
                    if (n.elementId === ElementType.WATER) {
                      hasWater = true;
                      waterCount++;
                    }
                    if (n.elementId === ElementType.DIRT || n.elementId === ElementType.MUD) hasSoil = true;
                    if (n.elementId === ElementType.MUD) isOptimalSoil = true;
                  }

                  const tempGood = particle.temperature > 10 && particle.temperature < 40;

                  if (hasWater && hasSoil && tempGood) {
                    const newAge = (particle.age || 0) + 1;
                    set(x, y, { ...particle, age: newAge });

                    if (newAge < 30 && Math.random() < 0.01) {
                      const dir = Math.random() < 0.5 ? -1 : 1;
                      if (!isOutOfBounds(x + dir, y) && get(x + dir, y).elementId === ElementType.EMPTY) {
                        const below = !isOutOfBounds(x + dir, y + 1) ? get(x + dir, y + 1) : null;
                        if (below && (below.elementId === ElementType.DIRT || below.elementId === ElementType.MUD)) {
                          set(x + dir, y, { elementId: ElementType.PLANT, age: 0, temperature: AMBIENT_TEMPERATURE });
                        }
                      }
                    }

                    if (newAge >= 30 && newAge < 100 && Math.random() < 0.015) {
                      if (!isOutOfBounds(x, y - 1) && get(x, y - 1).elementId === ElementType.EMPTY) {
                        set(x, y - 1, { elementId: ElementType.PLANT, age: 30, temperature: AMBIENT_TEMPERATURE });
                      }
                    }

                    if (newAge >= 100 && newAge < 200 && waterCount >= 1 && Math.random() < 0.008) {
                      const branchSpots = [[-1, 0], [1, 0], [-1, -1], [1, -1]];
                      for (const [dx, dy] of branchSpots) {
                        if (!isOutOfBounds(x + dx, y + dy) && get(x + dx, y + dy).elementId === ElementType.EMPTY) {
                          set(x + dx, y + dy, { elementId: ElementType.PLANT, age: 100, temperature: AMBIENT_TEMPERATURE });
                          break;
                        }
                      }
                    }

                    if (newAge >= 200 && isOptimalSoil && waterCount >= 2 && Math.random() < 0.003) {
                      const flowerSpots = [[0, -1], [-1, -1], [1, -1]];
                      for (const [dx, dy] of flowerSpots) {
                        if (!isOutOfBounds(x + dx, y + dy) && get(x + dx, y + dy).elementId === ElementType.EMPTY) {
                          set(x + dx, y + dy, { elementId: ElementType.FLOWER, lifetime: 300 + Math.random() * 200, temperature: AMBIENT_TEMPERATURE });
                          break;
                        }
                      }
                    }
                  }
                  break;
                }

                case ElementType.FLOWER: {
                  const newLifetimeFlower = (particle.lifetime || 200) - 1;
                  if (newLifetimeFlower <= 0) {
                    set(x, y, { elementId: ElementType.EMPTY, temperature: particle.temperature });
                  } else {
                    set(x, y, { ...particle, lifetime: newLifetimeFlower });
                  }
                  break;
                }

                // NEW ELEMENTS: Physics
                case ElementType.WAX: {
                  const waxDensity = getDensityById(ElementType.WAX);
                  if (!isOutOfBounds(x, y + 1) && !updated[y + 1][x] && getDensityById(newGrid[y + 1][x].elementId) < waxDensity) {
                    swap(x, y, x, y + 1);
                  }
                  break;
                }

                case ElementType.FUSE: {
                  if (particle.temperature >= 100) {
                    const newLifetime = (particle.lifetime || 100) - 1;
                    if (newLifetime <= 0) {
                      set(x, y, { elementId: ElementType.FIRE, lifetime: 20, temperature: FIRE_TEMPERATURE });
                    } else {
                      set(x, y, { ...particle, lifetime: newLifetime });
                      // Spread to adjacent fuse
                      const fuseNeighbors = [[0, 1], [0, -1], [1, 0], [-1, 0]];
                      for (const [dx, dy] of fuseNeighbors) {
                        if (isOutOfBounds(x + dx, y + dy)) continue;
                        const neighbor = newGrid[y + dy][x + dx];
                        if (neighbor.elementId === ElementType.FUSE && !neighbor.lifetime) {
                          set(x + dx, y + dy, { ...neighbor, temperature: 150 });
                        }
                      }
                    }
                  }
                  const fuseDensity = getDensityById(ElementType.FUSE);
                  if (!isOutOfBounds(x, y + 1) && !updated[y + 1][x] && getDensityById(newGrid[y + 1][x].elementId) < fuseDensity) {
                    swap(x, y, x, y + 1);
                  }
                  break;
                }

                case ElementType.COAL: {
                  const coalDensity = getDensityById(ElementType.COAL);
                  if (!isOutOfBounds(x, y + 1) && !updated[y + 1][x] && getDensityById(newGrid[y + 1][x].elementId) < coalDensity) {
                    swap(x, y, x, y + 1);
                  }
                  break;
                }

                case ElementType.SALT: {
                  const saltDensity = getDensityById(ElementType.SALT);
                  if (!isOutOfBounds(x, y + 1) && !updated[y + 1][x] && getDensityById(newGrid[y + 1][x].elementId) < saltDensity) {
                    swap(x, y, x, y + 1);
                  } else {
                    const dir = Math.random() > 0.5 ? 1 : -1;
                    if (!isOutOfBounds(x + dir, y + 1) && !updated[y + 1][x + dir] && getDensityById(newGrid[y + 1][x + dir].elementId) < saltDensity) {
                      swap(x, y, x + dir, y + 1);
                    }
                  }
                  // Dissolve in water
                  const waterNeighbors = [[0, 1], [0, -1], [1, 0], [-1, 0]];
                  for (const [dx, dy] of waterNeighbors) {
                    if (isOutOfBounds(x + dx, y + dy)) continue;
                    const neighbor = newGrid[y + dy][x + dx];
                    if (neighbor.elementId === ElementType.WATER && Math.random() < 0.05) {
                      set(x, y, { elementId: ElementType.EMPTY, temperature: AMBIENT_TEMPERATURE });
                      break;
                    }
                  }
                  break;
                }

                case ElementType.SLIME: {
                  const slimeDensity = getDensityById(ElementType.SLIME);
                  // Sticky behavior - moves slower
                  if (Math.random() < 0.5) {
                    if (!isOutOfBounds(x, y + 1) && !updated[y + 1][x] && getDensityById(newGrid[y + 1][x].elementId) < slimeDensity) {
                      swap(x, y, x, y + 1);
                    } else {
                      const dir = particle.vx || (Math.random() < 0.5 ? 1 : -1);
                      if (!isOutOfBounds(x + dir, y) && !updated[y][x + dir] && newGrid[y][x + dir].elementId === ElementType.EMPTY) {
                        swap(x, y, x + dir, y);
                        if (!isOutOfBounds(x + dir, y)) newGrid[y][x + dir].vx = dir;
                      }
                    }
                  }
                  break;
                }

                case ElementType.ELECTRIC_SPARK: {
                  const sparkLife = (particle.lifetime || 5) - 1;
                  if (sparkLife <= 0) {
                    set(x, y, { elementId: ElementType.EMPTY, temperature: AMBIENT_TEMPERATURE });
                  } else {
                    set(x, y, { ...particle, lifetime: sparkLife });
                    // Travel through metal
                    const sparkNeighbors = [[0, 1], [0, -1], [1, 0], [-1, 0]];
                    for (const [dx, dy] of sparkNeighbors) {
                      if (isOutOfBounds(x + dx, y + dy)) continue;
                      const neighbor = newGrid[y + dy][x + dx];
                      if (neighbor.elementId === ElementType.METAL) {
                        // Continue through metal
                        const nextX = x + dx * 2;
                        const nextY = y + dy * 2;
                        if (!isOutOfBounds(nextX, nextY) && newGrid[nextY][nextX].elementId === ElementType.EMPTY) {
                          set(nextX, nextY, { elementId: ElementType.ELECTRIC_SPARK, lifetime: 5, temperature: LIGHTNING_TEMPERATURE });
                        }
                      } else if (neighbor.elementId !== ElementType.EMPTY && neighbor.elementId !== ElementType.WALL) {
                        const neighborProps = elementMap.get(neighbor.elementId);
                        if (neighborProps?.flammable) {
                          set(x + dx, y + dy, { elementId: ElementType.FIRE, lifetime: 30, temperature: FIRE_TEMPERATURE });
                        }
                      }
                    }
                  }
                  break;
                }
              }
            }
          }

          // Update chunk sleep state
          updateChunkSleepState(chunkManager);

          return newGrid;
        });

        return updatedAgents;
      });
    };

    if (isPlaying) {
      const interval = SIMULATION_INTERVAL / settings.simulation.speed;
      const intervalId = window.setInterval(simulationTick, interval);
      return () => clearInterval(intervalId);
    }
  }, [isPlaying, elementMap, triggerScreenShake, settings.simulation.speed]);

  useEffect(() => {
    if (!isPlaying) return;
    const tempInterval = window.setInterval(() => {
      setGrid(g => {
        setRoomTemperature(calculateRoomTemperature(g));
        return g;
      });
    }, 500);
    return () => clearInterval(tempInterval);
  }, [isPlaying, calculateRoomTemperature]);

  const handleDraw = useCallback((x: number, y: number) => {
    if (x >= 0 && x < GRID_WIDTH && y >= 0 && y < GRID_HEIGHT) {
      const radius = Math.max(0, Math.floor(brushSize / 2));
      setGrid((prevGrid) => {
        const newGrid = [...prevGrid.map(row => [...row])];

        for (let dy = -radius; dy <= radius; dy++) {
          for (let dx = -radius; dx <= radius; dx++) {
            if (brushSize !== 1 && (dx * dx + dy * dy) > (radius * radius)) {
              continue;
            }

            const tx = x + dx;
            const ty = y + dy;
            if (tx < 0 || tx >= GRID_WIDTH || ty < 0 || ty >= GRID_HEIGHT) {
              continue;
            }

            const currentElement = newGrid[ty][tx].elementId;
            if (currentElement !== ElementType.WALL || selectedElementId === ElementType.EMPTY) {
              let newParticle: Particle = { elementId: selectedElementId, temperature: AMBIENT_TEMPERATURE };
              if (selectedElementId === ElementType.FIRE) {
                newParticle.lifetime = 100 + Math.random() * 50;
                newParticle.temperature = FIRE_TEMPERATURE;
              }
              if (selectedElementId === ElementType.LAVA) {
                newParticle.temperature = LAVA_TEMPERATURE;
              }
              if (selectedElementId === ElementType.MAGMA) {
                newParticle.temperature = LAVA_TEMPERATURE * 1.2;
              }
              if (selectedElementId === ElementType.ICE) {
                newParticle.temperature = -20;
              }
              if (selectedElementId === ElementType.FUSE) {
                newParticle.lifetime = 100;
              }
              if (selectedElementId === ElementType.COAL) {
                newParticle.lifetime = 300;
              }
              if (selectedElementId === ElementType.ELECTRIC_SPARK) {
                newParticle.lifetime = 5;
                newParticle.temperature = LIGHTNING_TEMPERATURE;
              }
              newGrid[ty][tx] = newParticle;
              markCellModified(chunkManagerRef.current, tx, ty);
            }
          }
        }

        return newGrid;
      });

      // Track used elements
      setUsedElements(prev => new Set(prev).add(selectedElementId));

      // Play placement sound occasionally
      if (Math.random() < 0.3) {
        audioManager.playPlaceSound(selectedElementId as ElementType);
      }
    }
  }, [selectedElementId, brushSize]);

  // Continuous drawing while holding pointer
  useEffect(() => {
    if (!isDrawing) return;
    const drawInterval = window.setInterval(() => {
      if (drawPosRef.current) {
        handleDraw(drawPosRef.current.x, drawPosRef.current.y);
      }
    }, SIMULATION_INTERVAL);
    return () => clearInterval(drawInterval);
  }, [isDrawing, handleDraw]);

  // Spell Handlers
  const handleCastTornado = (start: { x: number, y: number }, end: { x: number, y: number }) => {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    let vx = 0;
    let vy = 0;
    if (dist > 1) {
      const speed = 1.2;
      vx = (dx / dist) * speed;
      vy = (dy / dist) * speed;
    }

    const newTornadoAgent: TornadoAgent = {
      type: 'TORNADO',
      id: Date.now(),
      x: start.x,
      y: start.y,
      vx,
      vy,
      radius: Math.max(15, Math.min(35, dist * 0.6)),
      lifetime: 80 + Math.min(dist, 60),
      strength: Math.max(1.5, Math.min(dist / 12, 3)),
      rotation: Math.random() > 0.5 ? 1 : -1,
    };
    setAgents(prev => [...prev, newTornadoAgent]);
    audioManager.playSpellSound('TORNADO');
    wakeAllChunks(chunkManagerRef.current);
  };

  const handleCastLightning = (start: { x: number, y: number }, end: { x: number, y: number }) => {
    const getPointsOnLine = (x0: number, y0: number, x1: number, y1: number): { x: number, y: number }[] => {
      const points: { x: number, y: number }[] = [];
      const dx = Math.abs(x1 - x0);
      const dy = Math.abs(y1 - y0);
      const sx = (x0 < x1) ? 1 : -1;
      const sy = (y0 < y1) ? 1 : -1;
      let err = dx - dy;
      let x = x0;
      let y = y0;
      let maxIterations = 1 + dx + dy;
      while (maxIterations-- > 0) {
        points.push({ x, y });
        if ((x === x1) && (y === y1)) break;
        const e2 = 2 * err;
        if (e2 > -dy) { err -= dy; x += sx; }
        if (e2 < dx) { err += dx; y += sy; }
      }
      return points;
    };

    const generateJaggedPath = (startPos: { x: number; y: number }, endPos: { x: number; y: number }, maxOffset: number): { x: number; y: number }[] => {
      const dist = Math.sqrt((endPos.x - startPos.x) ** 2 + (endPos.y - startPos.y) ** 2);
      if (dist < 8) {
        return [startPos, endPos];
      }
      const midX = (startPos.x + endPos.x) / 2;
      const midY = (startPos.y + endPos.y) / 2;
      const offsetX = midX + (Math.random() - 0.5) * maxOffset;
      const offsetY = midY + (Math.random() - 0.5) * maxOffset;
      const midPoint = { x: offsetX, y: offsetY };
      const path1 = generateJaggedPath(startPos, midPoint, maxOffset / 1.5);
      const path2 = generateJaggedPath(midPoint, endPos, maxOffset / 1.5);
      return path1.slice(0, -1).concat(path2);
    };

    const path = generateJaggedPath(start, end, 20);

    const newLightningAgent: Agent = {
      type: 'LIGHTNING',
      id: Date.now(),
      path: path,
      lifetime: 10,
    };
    setAgents(prev => [...prev, newLightningAgent]);
    audioManager.playSpellSound('LIGHTNING');

    setGrid(prevGrid => {
      const newGrid: Grid = prevGrid.map(row => row.map(p => ({ ...p })));

      let allEffectPoints: { x: number, y: number }[] = [];
      for (let i = 0; i < path.length - 1; i++) {
        const p1 = path[i];
        const p2 = path[i + 1];
        const segmentPoints = getPointsOnLine(Math.round(p1.x), Math.round(p1.y), Math.round(p2.x), Math.round(p2.y));
        allEffectPoints.push(...segmentPoints);
      }

      const uniquePoints = Array.from(new Set(allEffectPoints.map(p => JSON.stringify(p)))).map(s => JSON.parse(s));

      for (const point of uniquePoints) {
        const x = point.x;
        const y = point.y;
        if (x >= 0 && x < GRID_WIDTH && y >= 0 && y < GRID_HEIGHT) {
          const particle = newGrid[y][x];
          particle.temperature = LIGHTNING_TEMPERATURE;
          const elementProps = elementMap.get(particle.elementId);

          if (elementProps?.flammable) {
            newGrid[y][x] = { elementId: ElementType.FIRE, lifetime: 50, temperature: FIRE_TEMPERATURE };
          } else if (particle.elementId === ElementType.SAND) {
            newGrid[y][x] = { elementId: ElementType.GLASS, temperature: LIGHTNING_TEMPERATURE };
          } else if (particle.elementId === ElementType.WATER) {
            newGrid[y][x] = { elementId: ElementType.STEAM, lifetime: 150, temperature: LIGHTNING_TEMPERATURE };
          } else if (particle.elementId === ElementType.METAL) {
            // Electric spark travels through metal
            const sparkNeighbors = [[0, 1], [0, -1], [1, 0], [-1, 0]];
            for (const [dx, dy] of sparkNeighbors) {
              const nx = x + dx;
              const ny = y + dy;
              if (nx >= 0 && nx < GRID_WIDTH && ny >= 0 && ny < GRID_HEIGHT) {
                if (newGrid[ny][nx].elementId === ElementType.EMPTY) {
                  newGrid[ny][nx] = { elementId: ElementType.ELECTRIC_SPARK, lifetime: 5, temperature: LIGHTNING_TEMPERATURE };
                }
              }
            }
          }
        }
      }
      return newGrid;
    });
    wakeAllChunks(chunkManagerRef.current);
  };

  const handleCastMeteor = (pos: { x: number; y: number }) => {
    const newMeteor: MeteorAgent = {
      type: 'METEOR',
      id: Date.now(),
      x: pos.x,
      y: 0,
      vx: (Math.random() - 0.5) * 0.5,
      vy: 1,
      size: 3 + Math.floor(Math.random() * 2),
      lifetime: 200,
    };
    setAgents(prev => [...prev, newMeteor]);
    audioManager.playSpellSound('METEOR');
    wakeAllChunks(chunkManagerRef.current);
  };

  const handleCastBlackHole = (start: { x: number; y: number }, end: { x: number; y: number }) => {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    const newBH: BlackHoleAgent = {
      type: 'BLACKHOLE',
      id: Date.now(),
      x: start.x,
      y: start.y,
      radius: Math.max(25, Math.min(50, dist)),
      strength: Math.max(2, Math.min(6, dist / 8)),
      lifetime: 100 + Math.min(dist * 2, 100),
    };
    setAgents(prev => [...prev, newBH]);
    audioManager.playSpellSound('BLACKHOLE');
    wakeAllChunks(chunkManagerRef.current);
  };

  const handleCastRainCloud = (pos: { x: number; y: number }) => {
    const newCloud: RainCloudAgent = {
      type: 'RAINCLOUD',
      id: Date.now(),
      x: pos.x,
      y: Math.min(pos.y, 15),
      vx: (Math.random() - 0.5) * 0.8,
      width: 20 + Math.random() * 15,
      lifetime: 300 + Math.random() * 200,
    };
    setAgents(prev => [...prev, newCloud]);
    audioManager.playSpellSound('RAINCLOUD');
  };

  const handleCastBomb = (pos: { x: number; y: number }) => {
    const bombAgent: BombAgent = {
      id: Date.now(),
      type: 'BOMB_AGENT',
      x: pos.x,
      y: pos.y,
      vy: 0,
      timer: 90,
      maxTimer: 90,
    };
    setAgents(prev => [...prev, bombAgent]);
    audioManager.playSpellSound('BOMB');
  };

  // NEW SPELL HANDLERS
  const handleCastFreezeRay = (start: { x: number, y: number }, end: { x: number, y: number }) => {
    const generateJaggedPath = (startPos: { x: number; y: number }, endPos: { x: number; y: number }, maxOffset: number): { x: number; y: number }[] => {
      const dist = Math.sqrt((endPos.x - startPos.x) ** 2 + (endPos.y - startPos.y) ** 2);
      if (dist < 8) {
        return [startPos, endPos];
      }
      const midX = (startPos.x + endPos.x) / 2;
      const midY = (startPos.y + endPos.y) / 2;
      const offsetX = midX + (Math.random() - 0.5) * maxOffset;
      const offsetY = midY + (Math.random() - 0.5) * maxOffset;
      const midPoint = { x: offsetX, y: offsetY };
      const path1 = generateJaggedPath(startPos, midPoint, maxOffset / 1.5);
      const path2 = generateJaggedPath(midPoint, endPos, maxOffset / 1.5);
      return path1.slice(0, -1).concat(path2);
    };

    const path = generateJaggedPath(start, end, 10);

    const newFreezeRayAgent: FreezeRayAgent = {
      type: 'FREEZE_RAY',
      id: Date.now(),
      path: path,
      lifetime: 15,
    };
    setAgents(prev => [...prev, newFreezeRayAgent]);
    audioManager.playSpellSound('FREEZE_RAY');
    wakeAllChunks(chunkManagerRef.current);
  };

  const handleCastEarthquake = (pos: { x: number; y: number }) => {
    const newEarthquake: EarthquakeAgent = {
      type: 'EARTHQUAKE',
      id: Date.now(),
      x: pos.x,
      y: pos.y,
      radius: 1,
      maxRadius: 40,
      lifetime: 60,
      intensity: 2,
    };
    setAgents(prev => [...prev, newEarthquake]);
    triggerScreenShake(3);
    audioManager.playSpellSound('EARTHQUAKE');
    wakeAllChunks(chunkManagerRef.current);
  };

  const handleCastTsunami = (pos: { x: number; y: number }) => {
    const newTsunami: TsunamiAgent = {
      type: 'TSUNAMI',
      id: Date.now(),
      x: 0,
      y: pos.y,
      vx: 2,
      width: GRID_HEIGHT,
      height: 20,
      lifetime: 100,
    };
    setAgents(prev => [...prev, newTsunami]);
    audioManager.playSpellSound('TSUNAMI');
    wakeAllChunks(chunkManagerRef.current);
  };

  const handlePlayPause = () => setIsPlaying(!isPlaying);
  
  const handleClear = () => {
    setGrid(createEmptyGrid());
    setAgents([]);
  };

  const handleToggleTask = (taskId: string) => {
    setTaskStatus(prev => {
      const currentStatus = prev[taskId];
      if (roadmapData.flatMap(p => p.tasks).find(t => t.id === taskId)?.status === 'done') {
        return prev;
      }
      const newStatus = currentStatus === 'todo' ? 'done' : 'todo';
      return { ...prev, [taskId]: newStatus };
    });
  };

  const handleSave = () => {
    const saveData = {
      version: 2,
      width: GRID_WIDTH,
      height: GRID_HEIGHT,
      timestamp: Date.now(),
      grid: grid.map(row => row.map(p => ({
        id: p.elementId,
        t: Math.round(p.temperature ?? AMBIENT_TEMPERATURE),
        l: p.lifetime ?? null,
        a: p.age ?? null,
        vx: p.vx ?? null,
        vy: p.vy ?? null,
      }))),
      agents: agents,
    };

    const blob = new Blob([JSON.stringify(saveData)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `magic_sandbox_save_${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleLoad = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target?.result as string);
        if (json.width !== GRID_WIDTH || json.height !== GRID_HEIGHT) {
          alert('Incompatible save file dimensions.');
          return;
        }

        const toParticle = (savedCell: any): Particle => {
          if (typeof savedCell === 'string') {
            return {
              elementId: savedCell,
              temperature: AMBIENT_TEMPERATURE,
            };
          }

          if (!savedCell || typeof savedCell !== 'object') {
            return {
              elementId: ElementType.EMPTY,
              temperature: AMBIENT_TEMPERATURE,
            };
          }

          const savedElementId = typeof savedCell.id === 'string' ? savedCell.id : ElementType.EMPTY;
          const savedTemp = typeof savedCell.t === 'number' ? savedCell.t : AMBIENT_TEMPERATURE;

          return {
            elementId: savedElementId,
            temperature: savedTemp,
            lifetime: typeof savedCell.l === 'number' ? savedCell.l : undefined,
            age: typeof savedCell.a === 'number' ? savedCell.a : undefined,
            vx: typeof savedCell.vx === 'number' ? savedCell.vx : undefined,
            vy: typeof savedCell.vy === 'number' ? savedCell.vy : undefined,
          };
        };

        setGrid(() => {
          const newGrid: Grid = new Array(GRID_HEIGHT);
          for (let y = 0; y < GRID_HEIGHT; y++) {
            newGrid[y] = new Array(GRID_WIDTH);
            for (let x = 0; x < GRID_WIDTH; x++) {
              const savedP = json.grid?.[y]?.[x];
              newGrid[y][x] = toParticle(savedP);
            }
          }
          return newGrid;
        });

        if (Array.isArray(json.agents)) {
          const loadedAgents = json.agents.filter((agent: any): agent is Agent =>
            agent &&
            typeof agent === 'object' &&
            typeof agent.id === 'number' &&
            typeof agent.type === 'string'
          );
          setAgents(loadedAgents);
        } else {
          setAgents([]);
        }

        setIsDrawing(false);
        drawPosRef.current = null;
      } catch (err) {
        console.error(err);
        alert('Failed to load save file.');
      }
    };
    reader.readAsText(file);
  };

  const handleSelectScene = (scene: typeof PREBUILT_SCENES[0]) => {
    const newGrid = scene.generate(GRID_WIDTH, GRID_HEIGHT);
    setGrid(newGrid);
    setAgents([]);
    setIsSceneSelectorOpen(false);
  };

  const handleClearData = () => {
    if (confirm('Are you sure you want to clear all data? This will reset your settings and canvas.')) {
      localStorage.removeItem('magic-sandbox-settings');
      setSettings(DEFAULT_SETTINGS);
      handleClear();
    }
  };

  return (
    <div className="h-screen w-screen overflow-hidden bg-gray-900 relative">
      <Toolbar
        elements={elements}
        selectedElementId={selectedElementId}
        setSelectedElementId={setSelectedElementId}
        brushSize={brushSize}
        setBrushSize={setBrushSize}
        isPlaying={isPlaying}
        onPlayPause={handlePlayPause}
        onClear={handleClear}
        onOpenRoadmap={() => setIsTodoListOpen(true)}
        onOpenSettings={() => setIsSettingsOpen(true)}
        onOpenSceneSelector={() => setIsSceneSelectorOpen(true)}
        onSave={handleSave}
        onLoad={handleLoad}
        usedElements={usedElements}
        navCollapsed={navCollapsed}
        onToggleNav={() => setNavCollapsed(c => !c)}
      />

      <main
        ref={canvasContainerRef}
        className="absolute inset-0 flex items-center justify-center transition-transform"
        style={{ transform: `translate(${screenShake.x}px, ${screenShake.y}px)` }}
      >
        <SimulationCanvas
          grid={grid}
          elements={elements}
          agents={agents}
          selectedElementId={selectedElementId}
          onDraw={handleDraw}
          onStartDraw={(x, y) => { setIsDrawing(true); drawPosRef.current = { x, y }; handleDraw(x, y); }}
          onStopDraw={() => { setIsDrawing(false); drawPosRef.current = null; }}
          onMoveDraw={(x, y) => { drawPosRef.current = { x, y }; handleDraw(x, y); }}
          onCastTornado={handleCastTornado}
          onCastLightning={handleCastLightning}
          onCastMeteor={handleCastMeteor}
          onCastBlackHole={handleCastBlackHole}
          onCastRainCloud={handleCastRainCloud}
          onCastBomb={handleCastBomb}
          onCastFreezeRay={handleCastFreezeRay}
          onCastEarthquake={handleCastEarthquake}
          onCastTsunami={handleCastTsunami}
          brushSize={brushSize}
          cellSize={cellSize}
          glowEffects={settings.graphics.glowEffects}
        />
      </main>

      <TemperatureDisplay roomTemperature={roomTemperature} />

      <TodoList
        isOpen={isTodoListOpen}
        onClose={() => setIsTodoListOpen(false)}
        taskStatus={taskStatus}
        onToggleTask={handleToggleTask}
      />

      <SettingsPanel
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        settings={settings}
        onSettingsChange={setSettings}
        onClearData={handleClearData}
        onOpenRoadmap={() => setIsTodoListOpen(true)}
      />

      <SceneSelector
        isOpen={isSceneSelectorOpen}
        onClose={() => setIsSceneSelectorOpen(false)}
        scenes={PREBUILT_SCENES}
        onSelectScene={handleSelectScene}
      />
    </div>
  );
};

export default App;
